import { Box, Chip, Icon, Paper, Skeleton, Stack } from "@mui/material";
import { useGridApiRef, DataGrid, GridSortModel, GridPaginationModel, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { MapBounds, PointResult, PointsSearch } from "points/models";
import { useEffect, useState } from "react";
import pointsService from "services/pointsService";
import ListFilter from "./ListFilter";
import { Link } from "react-router-dom";
import MDButton from "components/MDButton";
import { usePointPermissions } from "points/permissions";
import PointsMap from "./PointsMap";

/**
 * Ceiling on pins. There are ~65,000 client points, so a wide viewport can match far more than is
 * useful to draw -- the map reports how many it is holding back rather than silently truncating.
 */
const MAP_POINT_CAP = 500;

/**
 * Left over from when the show/hide-map choice was remembered across visits. Read once on mount
 * so an existing stored value is cleared rather than left to sit in browsers that already have
 * one -- otherwise the key lingers forever with nothing reading it.
 */
const LEGACY_MAP_VISIBLE_KEY = "points.mapVisible";

const PointsList = () => {
  const { canCreatePoint } = usePointPermissions();

  /**
   * The map starts hidden, every time.
   *
   * It used to remember the last choice in localStorage, and that turned out to be the wrong
   * behaviour: opening the map once meant it was open on every future visit, including for
   * someone who only wanted it that one time and had forgotten they ever clicked. The list is
   * what people come to this screen for -- the map is for confirming a location once you have
   * found it -- so "hidden" is the right thing to arrive at, and one click is a cheap way to ask
   * for the other thing.
   *
   * It also means no map data is fetched unless somebody asks for it: the 500-row query below is
   * gated on this flag.
   */
  const [mapVisible, setMapVisible] = useState<boolean>(false);

  // One-time cleanup of the key the old behaviour wrote. Harmless if it was never set.
  useEffect(() => {
    try {
      window.localStorage.removeItem(LEGACY_MAP_VISIBLE_KEY);
    } catch {
      // Private windows and blocked site data throw here; nothing depends on this succeeding.
    }
  }, []);

  const toggleMap = () => {
    setMapVisible((visible) => {
      const next = !visible;
      if (!next) {
        setMapPoints([]);
        setMapTotal(0);
      }
      return next;
    });
  };
  let persistedSearch = localStorage.getItem("points_list");
  let initialSearch: PointsSearch = {
    pagedSearchOptions: { offset: 0, pageSize: 25 },
    name: undefined,
    sortAsc: true,
    sortName: "PointId",
    // Active by default. A deactivated location is still a real row people occasionally need to
    // find -- that is what the Inactive and All options are for -- but it is not what someone
    // opening this screen is looking for, and showing them mixed together with no way to tell
    // them apart is how a switched-off location reads as a working one.
    isActive: true,
  };
  const [points, setPoints] = useState<PointResult[]>([]);
  // Typed PointsSearch, not OrderSearch. It has always HELD a PointsSearch -- initialSearch is
  // one -- but was declared as an order search, so every points-only field was invisible to the
  // compiler and any typo in one would have gone unnoticed.
  //
  // `bounds` is stripped off anything restored from storage. It used to live in here and older
  // browsers still have one saved; leaving it would silently filter the list to wherever the map
  // happened to be sitting on some previous visit, with nothing on screen saying so.
  const [search, setSearch] = useState<PointsSearch>(() => {
    if (!persistedSearch) return initialSearch;
    const restored = JSON.parse(persistedSearch) as PointsSearch;
    delete restored.bounds;
    // Saved before the status filter existed, so it has no opinion about it. Take the screen's
    // default rather than leaving it undefined, which is the "All" case and would quietly show
    // deactivated locations to anyone with an older saved filter.
    if (!("isActive" in restored)) restored.isActive = true;
    return restored;
  });

  /**
   * The map's viewport, kept OUT of the list's search.
   *
   * Panning or zooming the map does not filter the grid. The two answer different questions --
   * the grid answers "which locations match what I typed", the map answers "what is where I am
   * looking" -- and tying them together meant zooming in to read a label quietly threw rows out
   * of the list underneath, which reads as the list losing data rather than as a filter.
   */
  const [mapBounds, setMapBounds] = useState<MapBounds | undefined>(undefined);
  const [total, setTotal] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);

  // The map's own copy. Same filters as the list, plus the viewport, and a much larger page: the
  // grid shows 25 rows and a map showing 25 pins would be useless. Capped at MAP_POINT_CAP, and
  // the map says so when it is holding back.
  const [mapPoints, setMapPoints] = useState<PointResult[]>([]);
  const [mapTotal, setMapTotal] = useState<number>(0);
  const [isMapLoading, setIsMapLoading] = useState(false);
  const gridRef = useGridApiRef();
  const columns: GridColDef[] = [
    { field: "name", headerName: "Name", flex: 1, sortable: false, renderCell: (params: GridRenderCellParams<any, number>) => <Link to={`${params.row.pointId}`}>{params.value}</Link>, },
    { field: "clientName", headerName: "Client", flex:1},
    { field: "latitude", headerName: "Lat / Long", flex: 1, renderCell: (params: GridRenderCellParams<any, number>) => `${params.row.latitude} / ${params.row.longitude}` },
    { field: "address", headerName: "Address", flex: 1, renderCell: (params: GridRenderCellParams<any, number>) => `${params.row.address},  ${params.row.city}, ${params.row.state}` },
    {
      field: "isActive",
      headerName: "Active",
      width: 110,
      sortable: false,
      // Shown as a word, not a raw boolean: the grid is scanned, and "false" in a column of
      // addresses does not read as "this location is switched off".
      renderCell: (params: GridRenderCellParams<any, boolean>) => (
        <Chip
          size="small"
          variant="outlined"
          color={params.row.isActive ? "success" : "warning"}
          label={params.row.isActive ? "Active" : "Inactive"}
        />
      ),
    },
  ];

  const handleSortModelChange = (sortModel: GridSortModel) => {
    if (!(sortModel.length > 0)) return;
    var request = {
      ...search,
      sortName: sortModel[0].field,
      sortAsc: sortModel[0].sort == "asc",
      pagedSearchOptions: { ...search.pagedSearchOptions, offset: 0 },
    };
    setSearch(request);
    gridRef.current.setPage(0);
  };

  const handlePaginationModel = (pagination: GridPaginationModel) => {
    if (pagination.pageSize != search.pagedSearchOptions.pageSize) {
      pagination.page = 0;
    }
    setSearch({ ...search, pagedSearchOptions: { pageSize: pagination.pageSize, offset: pagination.page } });
  };

  useEffect(() => {
    // Rows are deliberately NOT cleared here. Blanking them made every refetch flash the grid
    // empty and then repopulate -- including refetches the user did not ask for. The stale page
    // stays put behind the loading state until the new one arrives.
    setIsLoading(true);
    localStorage.setItem("points_list", JSON.stringify(search));
    pointsService
      .search(search)
      .then((results) => {
        setPoints(results.results);
        setTotal(results.count);
      })
      .finally(() => setIsLoading(false));
  }, [search]);

  useEffect(() => {
    // No map on screen, no map query. Without this the 500-row fetch still ran on every filter
    // change for a component nobody is looking at.
    if (!mapVisible) return;
    if (!mapBounds) return;

    setIsMapLoading(true);
    pointsService
      // The map's own query: the same filters the grid is using, PLUS the viewport. The bounds go
      // on here and nowhere else, which is what keeps panning off the grid.
      .search({ ...search, bounds: mapBounds, pagedSearchOptions: { offset: 0, pageSize: MAP_POINT_CAP } })
      .then((results) => {
        setMapPoints(results.results ?? []);
        setMapTotal(results.count ?? 0);
      })
      .catch(() => {
        setMapPoints([]);
        setMapTotal(0);
      })
      .finally(() => setIsMapLoading(false));
    // Deliberately not keyed on the paging model: moving to page 2 of the list must not refetch
    // or redraw the map.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapVisible, mapBounds, search.name, search.clientIds, search.hubIds, search.isActive]);

  const clearFilters = () => {
    // The viewport is not a filter and is not cleared here -- it describes where the user is
    // LOOKING, which the filter buttons have no opinion about, and yanking the map back to the
    // whole country because someone cleared a name filter would be its own bug.
    setSearch(initialSearch);
  };

  /**
   * The map moved. Nothing but the map cares.
   *
   * No page reset either: the grid's rows did not change, so sending the user back to page 1
   * because they nudged the map would lose their place for no reason.
   */
  const handleBoundsChange = (bounds: MapBounds) => {
    setMapBounds(bounds);
  };

  const LoadingSkeleton = () => (
    <Box
      sx={{
        height: "fit-content",
        padding: "0px",
        margin: "0px",
        overflow: "hidden",
      }}
    >
      {[...Array(search.pagedSearchOptions.pageSize)].map((_, index) => (
        <Skeleton key={index} variant="rectangular" width="98%" sx={{ my: 3, mx: 1, overflow: "hidden" }} />
      ))}
    </Box>
  );
  return (
    <>
    <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"}>
        <ListFilter gridRef={gridRef} search={search} setSearch={setSearch} clearFilters={clearFilters} />
        <Box display={"flex"} alignItems={"center"}>
          {/* Sits with the other page actions rather than over the map, so the control is in the
              same place whether the map is up or not. */}
          <MDButton
            variant="outlined"
            color="info"
            onClick={toggleMap}
            startIcon={<Icon>{mapVisible ? "expand_less" : "map"}</Icon>}
            sx={{ mt: 2, mr: 2 }}
          >
            {mapVisible ? "Hide Map" : "Show Map"}
          </MDButton>
          {canCreatePoint && (
            <MDButton component={Link} to="new" variant="contained" color="primary" sx={{ mt: 2, mr: 2 }}>
              Add a Location
            </MDButton>
          )}
        </Box>
      </Box>
      <Box sx={{ mt: 2, width: "99%" }}>
        {mapVisible && (
          <Paper sx={{ p: 1 }}>
            <PointsMap
              points={mapPoints}
              onBoundsChange={handleBoundsChange}
              isLoading={isMapLoading}
              total={mapTotal}
              shown={mapPoints.length}
            />
          </Paper>
        )}
      </Box>

      <Box sx={{ mt: 2, overflow: "hidden" }}>
        <Paper sx={{ width: "99%", height: isLoading ? "100vh" : "auto", overflow: "hidden" }}>
          <DataGrid
            autoHeight={isLoading ? false : true}
            getRowId={(row) => row.pointId}
            sx={{ overflow: "hidden", fontSize: ".9em" }}
            rows={points}
            columns={columns}
            pagination
            sortingMode="server"
            paginationMode="server"
            onPaginationModelChange={handlePaginationModel}
            onSortModelChange={handleSortModelChange}
            initialState={{
              pagination: { paginationModel: search.pagedSearchOptions },
              sorting: { sortModel: [{ field: "pickupTime", sort: "asc" }] },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            rowCount={total}
            disableColumnMenu
            slots={{
              loadingOverlay: LoadingSkeleton,
              noRowsOverlay: () => (
                <Stack height="100%" alignItems="center" justifyContent="center">
                  No Points Match the filter criteria
                </Stack>
              ),
            }}
            loading={isLoading}
            density="compact"
            apiRef={gridRef as any}
            getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "Mui-even" : "Mui-odd")}
          />
        </Paper>
      </Box>
    </>
  );
};

export default PointsList;
